import { useState, useEffect, useRef } from 'react';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { useEnterAsTab } from '../hooks/useEnterAsTab';
import { AppModal } from '../components/AppModal';

const Row = ({ label, children }: any) => (
  <div className="flex items-center mb-2">
    <span className="w-[220px] text-[13px] text-gray-800 flex-shrink-0">{label}</span>
    <span className="w-[14px] text-center font-bold text-gray-700 flex-shrink-0">:</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

export const UnitCreationView = ({ handleItemSelect, unitId }: any) => {
  const containerRef  = useRef<HTMLDivElement>(null);
  const nameInputRef  = useRef<HTMLInputElement>(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const [symbol, setSymbol]               = useState('');
  const [formalName, setFormalName]       = useState('');
  const [decimalPlaces, setDecimalPlaces] = useState(0);
  const [accepting, setAccepting]         = useState(false);

  useEnterAsTab(containerRef);

  useEffect(() => {
    if (!accepting) return;
    const h = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'y' || e.key === 'Enter') { e.preventDefault(); e.stopImmediatePropagation(); doSave(); }
      else if (e.key.toLowerCase() === 'n' || e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); setAccepting(false); }
    };
    window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [accepting]);

  useEffect(() => {
    if (unitId) {
      db.units.get(unitId).then(u => {
        if (u) { setSymbol(u.symbol); setFormalName(u.formalName || ''); setDecimalPlaces(u.decimalPlaces || 0); }
      });
    } else { setSymbol(''); setFormalName(''); setDecimalPlaces(0); }
  }, [unitId]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'd') { e.preventDefault(); e.stopPropagation(); handleDelete(); }
    };
    if (unitId) window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [unitId]);

  const doSave = async () => {
    setAccepting(false);
    if (!activeCompany || !symbol.trim()) { alert('Symbol is required'); return; }
    if (unitId) {
      await db.units.update(unitId, { symbol: symbol.trim(), formalName, decimalPlaces });
      handleItemSelect({ name: 'Quit' });
    } else {
      const exists = await db.units.where('[companyId+symbol]').equals([activeCompany.id!, symbol.trim()]).first();
      if (exists) { alert('Already exists'); return; }
      await db.units.add({ companyId: activeCompany.id!, symbol: symbol.trim(), formalName, decimalPlaces });
      setSymbol(''); setFormalName(''); setDecimalPlaces(0);
      nameInputRef.current?.focus();
    }
  };

  const handleSave = () => setAccepting(true);

  const [pendingDelete, setPendingDelete] = useState(false);

  const handleDelete = async () => {
    if (!unitId) return;
    try {
      const usedCount = await db.stockItems.where('unitId').equals(unitId).count();
      if (usedCount > 0) { alert('Unit is used in Stock Items. Cannot delete.'); return; }
      setPendingDelete(true);
    } catch (error: any) { alert(error?.message || 'Failed to delete unit'); }
  };

  const confirmDelete = async () => {
    setPendingDelete(false);
    if (!unitId) return;
    try {
      await db.units.update(unitId, { isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: 'System' });
      handleItemSelect({ name: 'Quit' });
    } catch (error: any) { alert(error?.message || 'Failed to delete unit'); }
  };

  const quit = () => handleItemSelect({ name: 'Quit' });


  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm font-sans text-[13px]">
      <div ref={containerRef} className="flex flex-col bg-white relative overflow-hidden border border-[#444] rounded-sm"
        style={{ width: '600px', maxWidth: '98vw', maxHeight: '94vh' }}>

        <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px] flex-shrink-0">
          <span className="w-1/3 text-left">Unit {unitId ? 'Alteration' : 'Creation'}</span>
          <span className="w-1/3 text-center uppercase">{activeCompany?.name || ''}</span>
          <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" data-quit-btn onClick={quit}>✕</span>
        </div>

        <div className="flex border-b border-[#c8d4d4] bg-[#f9fafb] flex-shrink-0 p-4">
          <div className="flex items-center w-full">
            <span className="w-[150px] flex-shrink-0">Symbol</span>
            <span className="w-[14px] text-center font-bold flex-shrink-0">:</span>
            <input ref={nameInputRef} type="text" value={symbol} onChange={e => setSymbol(e.target.value)}
              className="flex-1 max-w-[200px] border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold uppercase"
              autoFocus />
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto bg-[#fdfdfd]">
          <Row label="Formal Name">
            <input type="text" value={formalName} onChange={e => setFormalName(e.target.value)}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[280px] focus:bg-yellow-50" />
          </Row>
          <Row label="Number of decimal places">
            <input type="number" value={decimalPlaces} onChange={e => setDecimalPlaces(parseInt(e.target.value) || 0)} min={0} max={4}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[60px] focus:bg-yellow-50" />
          </Row>
        </div>

        <div className="flex justify-between items-center bg-[#ebebeb] border-t border-[#b8b8b8] px-4 py-2 text-[12.5px] flex-shrink-0 h-[33px]">
          <div />
          <div className="flex items-center ml-auto gap-2">
            {unitId && (
              <button type="button" onClick={handleDelete} tabIndex={-1}
                className="px-3 py-0.5 border border-gray-400 text-gray-700 bg-[#e0e0e0] hover:bg-[#d0d0d0] rounded text-[11.5px] font-semibold"
                title="Alt + D to Delete"><u>D</u>elete</button>
            )}
            <button type="button" onClick={handleSave} tabIndex={-1} className="px-4 py-1 bg-[#1e8585] text-white font-bold rounded text-[11.5px] hover:bg-[#166e6e] shadow-sm mr-2">✓ Accept (Ctrl+A)</button><span className="text-gray-500 italic text-[11px]">Enter = Next · Esc = Cancel</span>
          </div>
        </div>

        {accepting && (
          <div className="absolute inset-0 bg-black/10 z-50 flex items-center justify-center">
            <div className="border-2 border-[#1e8585] bg-white px-16 py-6 shadow-[8px_8px_0_rgba(30,133,133,0.25)] text-center min-w-[200px] rounded-sm">
              <span className="block text-[17px] font-bold mb-4 text-black">Accept ?</span>
              <div className="font-bold text-[15px]">
                <span className="text-[#1e8585] cursor-pointer hover:underline" onClick={doSave}><u>Y</u>es</span>
                &nbsp;&nbsp;or&nbsp;&nbsp;
                <span className="text-gray-500 cursor-pointer hover:underline" onClick={() => setAccepting(false)}><u>N</u>o</span>
              </div>
            </div>
          </div>
        )}
        <button data-save-btn onClick={handleSave} className="hidden" />

        <AppModal
          open={pendingDelete}
          title="Delete Unit"
          type="warning"
          message="Are you sure you want to delete this unit?"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(false)}
        />
      </div>
    </div>
  );
};
